package com.shadowings.gero.presentation.component.avatars

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.size
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.presentation.screen.characters.CharacterConsts.BG_KEY
import com.shadowings.gero.presentation.screen.characters.CharacterConsts.GLASSES_ACCENT_KEY
import com.shadowings.gero.presentation.screen.characters.CharacterConsts.GLASSES_KEY
import com.shadowings.gero.presentation.screen.characters.CharacterConsts.GLASSES_TYPE_KEY
import com.shadowings.gero.presentation.screen.characters.backgroundOptions
import com.shadowings.gero.presentation.screen.characters.hairOptions
import com.shadowings.gero.presentation.screen.characters.shirtOptions
import com.shadowings.gero.presentation.screen.characters.skinOptions
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.ui.neutralBlack
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.zeta_dress_0
import gero.composeapp.generated.resources.zeta_dress_1
import gero.composeapp.generated.resources.zeta_dress_2
import gero.composeapp.generated.resources.zeta_dress_3
import gero.composeapp.generated.resources.zeta_eyebrows
import gero.composeapp.generated.resources.zeta_eyebrows_doubt
import gero.composeapp.generated.resources.zeta_eyebrows_idle
import gero.composeapp.generated.resources.zeta_glasses
import gero.composeapp.generated.resources.zeta_glasses_cover
import gero.composeapp.generated.resources.zeta_glasses_cyclop
import gero.composeapp.generated.resources.zeta_glasses_cyclop_accent
import gero.composeapp.generated.resources.zeta_glasses_gold
import gero.composeapp.generated.resources.zeta_glasses_pixel
import gero.composeapp.generated.resources.zeta_hair
import gero.composeapp.generated.resources.zeta_mouth_doubt
import gero.composeapp.generated.resources.zeta_mouth_idle
import gero.composeapp.generated.resources.zeta_mouth_surprise_0
import gero.composeapp.generated.resources.zeta_nose
import gero.composeapp.generated.resources.zeta_skin
import org.jetbrains.compose.resources.DrawableResource

@Preview
@Composable
fun ZetaAvatarPreview() {
    AppTheme {
        FlowRow {
            Faces.entries.forEach { face ->
                Column {
                    ZetaAvatar(
                        face = face,
                        withBackground = true
                    )
                }
            }
        }
    }
}

@Composable
fun ZetaAvatar(
    userData: UserData = UserData(),
    face: Faces = Faces.Idle,
    modifier: Modifier = Modifier.size(256.dp),
    withBackground: Boolean = true
) {
    val bgColorIndex =
        (userData.zetaColors[BG_KEY] ?: 0).coerceIn(0, backgroundOptions.size - 1)
    val skinColorIndex = (userData.zetaColors["skin"] ?: 0).coerceIn(0, skinOptions.size - 1)
    val hairColorIndex = (userData.zetaColors["hair"] ?: 9).coerceIn(0, hairOptions.size - 1)
    val dressColorIndex = (userData.zetaColors["dress"] ?: 5).coerceIn(0, shirtOptions.size - 1)
    val glassesColorIndex = (userData.zetaColors[GLASSES_KEY] ?: 9).coerceIn(0, hairOptions.size - 1)
    val glassesTypeIndex = (userData.zetaColors[GLASSES_TYPE_KEY] ?: 0).coerceIn(0, 3)
    val glassesAccentColorIndex =
        (userData.zetaColors[GLASSES_ACCENT_KEY] ?: 7).coerceIn(0, hairOptions.size - 1)

    val skinColor = skinOptions[skinColorIndex]
    val hairColor = hairOptions[hairColorIndex]
    val dressColor = shirtOptions[dressColorIndex]
    val glassesColor = hairOptions[glassesColorIndex]

    val noseColor = skinColor.reddish(0.9f)
    val mouthColor = neutralBlack
    val eyebrowsColor = hairColor.darken()

    // index 0 = base "zeta_glasses" (today's default), 1 = gold, 2 = pixel, 3 = ciclope (main +
    // a separately tintable red accent stripe).
    val glassesLensPairs: List<Pair<DrawableResource, Color>> = when (glassesTypeIndex) {
        1 -> listOf(Res.drawable.zeta_glasses_gold to glassesColor)
        2 -> listOf(Res.drawable.zeta_glasses_pixel to glassesColor)
        3 -> listOf(
            Res.drawable.zeta_glasses_cyclop to glassesColor,
            Res.drawable.zeta_glasses_cyclop_accent to hairOptions[glassesAccentColorIndex],
        )

        else -> listOf(Res.drawable.zeta_glasses to glassesColor)
    }

    val pairs: List<Pair<DrawableResource, Color>> = listOf(
        Res.drawable.zeta_dress_0 to dressColor,
        Res.drawable.zeta_dress_1 to dressColor.lighten(0.5f),
        Res.drawable.zeta_dress_2 to dressColor,
        Res.drawable.zeta_dress_3 to dressColor.lighten(0.25f),
        Res.drawable.zeta_nose to noseColor,
        Res.drawable.zeta_hair to hairColor,
        Res.drawable.zeta_skin to skinColor,
        Res.drawable.zeta_glasses_cover to skinColor,
    ) + glassesLensPairs

    val expression = when (face) {
        Faces.Idle -> {
            listOf(
                Res.drawable.zeta_mouth_idle to mouthColor,
                Res.drawable.zeta_eyebrows_idle to eyebrowsColor
            )
        }

        Faces.Surprised -> {
            listOf(
                Res.drawable.zeta_mouth_surprise_0 to mouthColor,
                Res.drawable.zeta_eyebrows_idle to eyebrowsColor
            )
        }

        Faces.Doubt -> {
            listOf(
                Res.drawable.zeta_mouth_doubt to mouthColor,
                Res.drawable.zeta_eyebrows_doubt to eyebrowsColor
            )
        }

        else -> {
            listOf(
                Res.drawable.zeta_mouth_idle to mouthColor,
                Res.drawable.zeta_eyebrows to eyebrowsColor
            )
        }
    }

    BuildAvatar(withBackground, bgColorIndex, pairs, expression, modifier, face)
}
